import {
  MAX_TIMETABLE_FILES,
  TIMETABLE_FILES_MAX_BYTES,
  type TeamMember,
  type Timetable,
  type TimetableCard,
  type TimetableFile,
} from "@teamna/shared";
import { useMemo, useState, type Dispatch, type SetStateAction } from "react";
import {
  Avatar,
  Badge,
  Button,
  CellField,
  DataTable,
  FileField,
  InlineError,
  Notice,
  PlusCircleIcon,
  Select,
  SkeletonList,
  TrashIcon,
  showToast,
  type DataTableColumn,
} from "../../components/index.ts";
import { useLabels } from "@teamna/web-client/hooks/use-labels.ts";
import {
  useExtractTimetable,
  useSaveTimetable,
  useTeam,
  useTimetable,
  useTimetableTemplate,
} from "@teamna/web-client/hooks/use-setup.ts";
import { SaveBar, SetupFrame, WeekdayToggles } from "./frame.tsx";
import { needsShrink, timetableFile } from "./timetable-file.ts";

/**
 * Setup step 4: groups and sessions (mobile spec 5.3, "Define sessions"), as a desktop page.
 *
 * Two ways in: upload timetable photos or a PDF (up to six files, the pages of one timetable), which
 * `/timetable/extract` reads together (the sport template, flagged simulated, when no reader is
 * configured), or "Set it up by hand" from the sport template.
 * Both end on the same review table: one row per group and start time, with duration, places,
 * weekday toggles (days off disabled) and a staff select. Save sends the whole table to
 * `PUT /timetable`, which creates the groups and slots and generates their sessions (a new timetable gets the default end of term); the
 * step is done once one weekly slot exists. Slice C replaces this page with the full groups page.
 */
export function SetupSessionsScreen() {
  return <SetupFrame step="sessions">{(businessId) => <SessionsBody businessId={businessId} />}</SetupFrame>;
}

type Draft = TimetableCard & { key: string };

let draftCounter = 0;
function withKeys(cards: TimetableCard[]): Draft[] {
  return cards.map((card) => ({ ...card, key: `d${++draftCounter}` }));
}

/** The review rows for a saved timetable: one per group per start time and coach. Mobile's rule. */
export function cardsFromTimetable(timetable: Timetable): TimetableCard[] {
  const cards: TimetableCard[] = [];
  for (const group of timetable.groups) {
    // One card per start time and coach: a level with two coaches at 18:00 gives two cards, so a
    // save keeps the coach of each weekly time.
    const byTime = new Map<string, { startTime: string; staffUserId: string | null; weekdays: number[] }>();
    for (const slot of group.slots) {
      const key = `${slot.startTime}|${slot.staffUserId ?? ""}`;
      const card = byTime.get(key) ?? { startTime: slot.startTime, staffUserId: slot.staffUserId, weekdays: [] };
      card.weekdays.push(slot.weekday);
      byTime.set(key, card);
    }
    if (byTime.size === 0) byTime.set("17:00", { startTime: "17:00", staffUserId: group.staffUserId, weekdays: [] });
    for (const { startTime, staffUserId, weekdays } of byTime.values()) {
      cards.push({
        groupId: group.id,
        name: group.name,
        kind: group.kind,
        startTime,
        durationMin: group.durationMin,
        capacity: group.capacity,
        weekdays,
        staffUserId,
        colorIndex: group.colorIndex,
      });
    }
  }
  return cards;
}

const TIME = /^([01]\d|2[0-3]):[0-5]\d$/;

function timetableColumns({
  label,
  update,
  daysOff,
  staffOptions,
  setDrafts,
}: {
  label: ReturnType<typeof useLabels>["label"];
  update: (key: string, patch: Partial<TimetableCard>) => void;
  daysOff: number[];
  staffOptions: TeamMember[];
  setDrafts: Dispatch<SetStateAction<Draft[] | null>>;
}): DataTableColumn<Draft>[] {
  return [
    {
      id: "name",
      header: label("group", { capital: true }),
      width: "170px",
      cell: (row) => (
        <div className="flex flex-col gap-1">
          <CellField
            className="min-w-[140px]"
            label={`${label("group", { capital: true })} name`}
            value={row.name}
            onChange={(event) => update(row.key, { name: event.target.value })}
          />
          {row.kind === "private" ? <Badge tone="quiet">Private slot</Badge> : null}
        </div>
      ),
    },
    {
      id: "start",
      header: "Start",
      width: "120px",
      cell: (row) => (
        <CellField
          label={`Start time for ${row.name}`}
          type="time"
          value={row.startTime}
          onChange={(event) => update(row.key, { startTime: event.target.value })}
        />
      ),
    },
    {
      id: "duration",
      header: "Minutes",
      width: "80px",
      cell: (row) => (
        <CellField
          label={`Minutes for ${row.name}`}
          type="number"
          min={5}
          max={600}
          value={row.durationMin}
          onChange={(event) => update(row.key, { durationMin: Number(event.target.value) || 0 })}
        />
      ),
    },
    {
      id: "places",
      header: "Places",
      width: "88px",
      // An empty field is no limit (null). A private group is locked to one.
      cell: (row) => (
        <CellField
          label={`Places for ${row.name}`}
          type="number"
          min={1}
          max={500}
          placeholder="No limit"
          value={row.capacity ?? ""}
          disabled={row.kind === "private"}
          onChange={(event) =>
            update(row.key, { capacity: event.target.value === "" ? null : Number(event.target.value) || 0 })
          }
        />
      ),
    },
    {
      id: "days",
      header: "Days",
      cell: (row) => (
        <WeekdayToggles
          compact
          label={`Days for ${row.name}`}
          selected={row.weekdays}
          disabledDays={daysOff}
          onChange={(weekdays) => update(row.key, { weekdays })}
        />
      ),
    },
    {
      id: "staff",
      header: label("staff", { capital: true }),
      width: "170px",
      cell: (row) => {
        // The coach's face before the choice; a select cannot hold a picture.
        const coach = staffOptions.find((member) => member.userId === row.staffUserId);
        return (
          <span className="flex items-center gap-1.5">
            {coach ? <Avatar name={coach.name} photoUrl={coach.photoUrl ?? null} size={24} /> : null}
            <Select
              label={`${label("staff", { capital: true })} for ${row.name}`}
              className="w-full min-w-[120px]"
              value={row.staffUserId ?? ""}
              onChange={(event) => update(row.key, { staffUserId: event.target.value || null })}
            >
              <option value="">Later</option>
              {staffOptions.map((member) => (
                <option key={member.userId!} value={member.userId!}>
                  {member.name}
                </option>
              ))}
            </Select>
          </span>
        );
      },
    },
    {
      id: "remove",
      header: "",
      align: "end",
      width: "48px",
      cell: (row) => (
        <button
          type="button"
          aria-label={`Remove ${row.name}`}
          onClick={() => setDrafts((list) => (list ?? []).filter((item) => item.key !== row.key))}
          className="flex size-9 items-center justify-center rounded-lg text-ink-3 hover:bg-surface hover:text-cancelled"
        >
          <TrashIcon size={17} />
        </button>
      ),
    },
  ];
}

function SessionsBody({ businessId }: { businessId: string }) {
  const { label } = useLabels();
  const groupWord = label("group");
  const staffWord = label("staff");
  const timetable = useTimetable(businessId);
  const template = useTimetableTemplate(businessId);
  const team = useTeam(businessId);
  const extract = useExtractTimetable(businessId);
  const save = useSaveTimetable(businessId);

  // A saved timetable opens straight on its review table; the owner's edits replace it.
  const saved = useMemo(
    () =>
      timetable.data && timetable.data.groups.length > 0
        ? withKeys(cardsFromTimetable(timetable.data))
        : null,
    [timetable.data],
  );
  const [edited, setEdited] = useState<Draft[] | null>(null);
  const drafts = edited ?? saved;
  const setDrafts: Dispatch<SetStateAction<Draft[] | null>> = (change) =>
    setEdited((list) => (typeof change === "function" ? change(list ?? saved) : change));
  const [simulated, setSimulated] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [preparing, setPreparing] = useState(false);

  const daysOff = timetable.data?.daysOff ?? [];
  const staffOptions = (team.data ?? []).filter((member) => member.userId !== null);

  async function readTimetable(files: File[]) {
    if (files.length > MAX_TIMETABLE_FILES) {
      setProblem(`Choose ${MAX_TIMETABLE_FILES} files or less. You chose ${files.length}.`);
      return;
    }
    setProblem(null);
    // Busy from the first read, so the owner cannot pick a second set while this one is prepared.
    setPreparing(true);
    try {
      const tooLarge = (bytes: number) =>
        setProblem(
          `These files are too large together (${Math.ceil(bytes / 1048576)} MB). Choose fewer pages or a smaller PDF, up to ${TIMETABLE_FILES_MAX_BYTES / 1048576} MB.`,
        );
      // Files that go as they are (PDFs, small images) are counted before any is read.
      const unchanged = files.reduce((total, file) => total + (needsShrink(file) ? 0 : file.size), 0);
      if (unchanged > TIMETABLE_FILES_MAX_BYTES) {
        tooLarge(unchanged);
        return;
      }
      // One file at a time, so a page never holds more than the limit. Large photos are made smaller
      // first: the reader refuses an image over 5 MB.
      const sent: TimetableFile[] = [];
      let bytes = 0;
      for (const file of files) {
        // oxlint-disable-next-line no-await-in-loop -- one file at a time, so the budget holds
        const prepared = await timetableFile(file, TIMETABLE_FILES_MAX_BYTES - bytes);
        if (!prepared.fits) {
          tooLarge(bytes + prepared.bytes);
          return;
        }
        bytes += prepared.bytes;
        sent.push(prepared.file);
      }
      const answer = await extract.mutateAsync({ files: sent });
      const off = new Set(daysOff);
      setDrafts(
        withKeys(
          answer.groups.map((group, index) => ({
            name: group.name,
            kind: "group" as const,
            startTime: group.startTime,
            durationMin: group.durationMin,
            // Places are open unless the timetable shows a number.
            capacity: group.capacity ?? null,
            weekdays: group.weekdays.filter((day) => !off.has(day)),
            staffUserId: null,
            colorIndex: index % 12,
          })),
        ),
      );
      setSimulated(answer.simulated ? answer.summary : null);
    } catch {
      setProblem(
        files.length === 1
          ? "We could not read that file. Try a clear photo, a screenshot or a PDF, or set it up by hand."
          : "We could not read those files. Try clear photos, screenshots or a PDF, or set it up by hand.",
      );
    } finally {
      setPreparing(false);
    }
  }

  function update(key: string, patch: Partial<TimetableCard>) {
    setDrafts((list) => (list ?? []).map((row) => (row.key === key ? Object.assign({}, row, patch) : row)));
  }

  function addRow() {
    setDrafts((list) => {
      const current = list ?? [];
      return [
        ...current,
        ...withKeys([
          {
            name: `${label("group", { capital: true })} ${current.length + 1}`,
            kind: "group",
            startTime: "17:00",
            durationMin: 45,
            capacity: null,
            weekdays: [],
            staffUserId: null,
            colorIndex: current.length % 12,
          },
        ]),
      ];
    });
  }

  function submit() {
    if (!drafts) return;
    const bad = drafts.find(
      (card) => !card.name.trim() || card.weekdays.length === 0 || !TIME.test(card.startTime),
    );
    if (bad) {
      setProblem(
        `"${bad.name || `A ${groupWord}`}" needs a name, a start time such as 17:00, and at least one day.`,
      );
      return;
    }
    setProblem(null);
    save.mutate(
      drafts.map(({ key: _key, ...card }) => ({ ...card, name: card.name.trim() })),
      {
        onSuccess: () => {
          setEdited(null);
          showToast("Sessions saved. They are on the schedule.");
        },
      },
    );
  }

  if (timetable.isLoading) return <SkeletonList rows={4} />;

  if (preparing || extract.isPending) {
    return (
      <div className="flex flex-col items-center gap-3 rounded-card border border-line bg-paper px-6 py-14 text-center">
        <output
          className="size-6 animate-spin rounded-full border-2 border-line-strong border-t-accent"
          aria-label="Reading"
        />
        <h2 className="text-[19px]">Reading your schedule</h2>
        <p className="text-[14px] text-ink-2">
          {label("group", { plural: true, capital: true })}, days and times. The files are not kept.
        </p>
      </div>
    );
  }

  const withoutStaff = (drafts ?? []).filter((card) => !card.staffUserId).length;

  const columns = timetableColumns({ label, update, daysOff, staffOptions, setDrafts });

  const entry = (
    <div className="grid gap-4 md:grid-cols-2">
      <section className="flex flex-col gap-3 rounded-card border border-line bg-paper p-5 shadow-card">
        <h2 className="text-[17px]">Upload your schedule</h2>
        <p className="text-[14px] text-ink-2">
          Photos, screenshots or a PDF. When your schedule has more than one page, choose all the pages
          together (up to {MAX_TIMETABLE_FILES}). We read the {label("group", { plural: true })}, the days
          and the times. The files are not kept.
        </p>
        <FileField
          className="mt-auto"
          fullWidth
          label="Schedule files"
          hideLabel
          accept="image/*,application/pdf"
          multiple
          buttonLabel="Choose photos or a PDF"
          onFiles={(files) => void readTimetable(files)}
        />
      </section>
      <section className="flex flex-col gap-3 rounded-card border border-line bg-paper p-5 shadow-card">
        <h2 className="text-[17px]">Set it up by hand</h2>
        <p className="text-[14px] text-ink-2">
          Start from the starter schedule for your sport, then change the names, days and times.
        </p>
        <Button
          variant="secondary"
          disabled={!template.data}
          onClick={() => {
            setSimulated(null);
            setDrafts(withKeys(template.data ?? []));
          }}
        >
          Set it up by hand
        </Button>
      </section>
    </div>
  );

  return (
    <div className="flex flex-col gap-6">
      {drafts === null ? entry : null}

      {problem ? <Notice tone="warning" title={problem} /> : null}

      {drafts !== null ? (
        <>
          {simulated ? (
            <Notice tone="info" title="The schedule reader is not connected yet">
              This is the starter schedule for your sport, not what you uploaded. Change anything below.
            </Notice>
          ) : null}
          {timetable.data && timetable.data.groups.length > 0 ? (
            <p className="text-[14px] text-ink-2">
              Saved now: {timetable.data.groups.length}{" "}
              {timetable.data.groups.length === 1 ? label("group") : label("group", { plural: true })},{" "}
              {timetable.data.groups.reduce((sum, group) => sum + group.slots.length, 0)} sessions a week. A
              save changes future sessions only.
            </p>
          ) : null}
          {withoutStaff > 0 ? (
            <p className="text-[14px] text-ink-2">
              {withoutStaff} {withoutStaff === 1 ? "row has" : "rows have"} no {staffWord}. You can choose one
              later from the schedule.
            </p>
          ) : null}
          <DataTable
            caption="Weekly schedule"
            density="compact"
            columns={columns}
            rows={drafts}
            rowKey={(row) => row.key}
            empty={{ title: "No rows", message: `Add a ${groupWord} to start the schedule.` }}
            countLabel={(count) => `${count} ${count === 1 ? "row" : "rows"}`}
          />
          <div>
            <Button variant="secondary" onClick={addRow}>
              <span className="inline-flex items-center gap-2">
                <PlusCircleIcon size={17} />
                Add a {groupWord} or a second time
              </span>
            </Button>
          </div>
          <SaveBar note="Sessions come from this table. You set the end of the term on the Schedule page.">
            <Button loading={save.isPending} disabled={drafts.length === 0} onClick={submit}>
              {withoutStaff > 0 ? `Save, choose ${label("staff", { plural: true })} later` : "Save sessions"}
            </Button>
            {timetable.data && timetable.data.groups.length === 0 ? (
              <Button variant="ghost" onClick={() => setDrafts(null)}>
                Start again
              </Button>
            ) : null}
          </SaveBar>
          {save.error ? <InlineError error={save.error} /> : null}
          {timetable.data && timetable.data.groups.length > 0 ? (
            <details className="text-[14px] text-ink-2">
              <summary className="cursor-pointer font-semibold text-accent-strong">
                Replace it from a new schedule
              </summary>
              <div className="mt-4">{entry}</div>
            </details>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
